import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { ReactComponent as CopyIcon } from '../../assets/copy-new.svg';
import styles from './Enable2FASetup.module.scss';

export const Enable2FASetup = ({
  otpData,
  onCodeChange,
  code,
  error,
}) => {
  const { t } = useTranslation();
  const [qrcodeURL, setQrcodeURL] = useState(null);
  const [copied, setCopied] = useState(false);
  const inputRefs = useRef([...Array(6)].map(() => null));

  useEffect(() => {
    if (otpData?.otp_2fa_url) {
      QRCode.toDataURL(otpData.otp_2fa_url)
        .then(setQrcodeURL)
        .catch(() => {});
    }
  }, [otpData?.otp_2fa_url]);

  const handleDigitChange = useCallback((index, value) => {
    if (value !== '' && isNaN(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    onCodeChange(newCode);

    if (index < 5 && value !== '') {
      inputRefs.current[index + 1]?.focus();
    }
  }, [code, onCodeChange]);

  const handleKeyDown = useCallback((index, e) => {
    if (e.key === 'Backspace' && index > 0 && code[index] === '') {
      inputRefs.current[index - 1]?.focus();
    }
  }, [code]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;

    const newCode = [...code];
    for (let i = 0; i < 6; i++) {
      newCode[i] = pasted[i] || '';
    }
    onCodeChange(newCode);

    const focusIndex = Math.min(pasted.length, 5);
    inputRefs.current[focusIndex]?.focus();
  }, [code, onCodeChange]);

  const handleCopySecret = useCallback(() => {
    if (otpData?.otp_2fa_secret) {
      navigator.clipboard.writeText(otpData.otp_2fa_secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [otpData?.otp_2fa_secret]);

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>{t('Get an authenticator app')}</h4>
        <p className={styles.sectionDescription}>
          {t("You'll need a mobile authenticator app on your phone. Download one if you don't have it:")}
        </p>
        <div className={styles.appLinks}>
          <a
            href="https://support.google.com/accounts/answer/1066447"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google Authenticator
          </a>
          <a
            href="https://www.microsoft.com/en-us/security/mobile-authenticator-app"
            target="_blank"
            rel="noopener noreferrer"
          >
            Microsoft Authenticator
          </a>
          <a
            href="https://help.okta.com/oie/en-us/content/topics/identity-engine/authenticators/configure-okta-verify.htm"
            target="_blank"
            rel="noopener noreferrer"
          >
            Okta Verify
          </a>
        </div>
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>{t('Scan QR code')}</h4>
        <p className={styles.sectionDescription}>
          {t('Scan the QR code below or manually enter the secret key into your authenticator app.')}
        </p>
        <div className={styles.qrCard}>
          <div className={styles.qrCodeArea}>
            {qrcodeURL && (
              <img src={qrcodeURL} alt={t('QR code for authenticator app')} />
            )}
          </div>
          <div className={styles.qrDetails}>
            <span className={styles.qrDetailsLabel}>
              {t("Can't scan? Enter code manually:")}
            </span>
            <div className={styles.secretKeyInput}>
              <span>{otpData?.otp_2fa_secret}</span>
            </div>
            <div>
              <button
                type="button"
                className={styles.copyButton}
                onClick={handleCopySecret}
                aria-label={t('Copy secret key')}
              >
                <CopyIcon />
                {copied ? t('Copied') : t('Copy code')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>{t('Enter verification code')}</h4>
        <p className={styles.sectionDescription}>
          {t('Enter the 6-digit code from your authenticator app.')}
        </p>
        <div className={styles.codeInputs} onPaste={handlePaste} role="group" aria-label={t('Verification code')}>
          {code.map((digit, index) => (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleDigitChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              ref={(el) => { inputRefs.current[index] = el; }}
              aria-label={`${t('Digit')} ${index + 1}`}
              autoComplete="one-time-code"
            />
          ))}
        </div>
        {error && (
          <div className={styles.errorMessage} role="alert">
            {t(error)}
          </div>
        )}
      </div>
    </div>
  );
};
